import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  TemplateRef,
  ViewContainerRef,
  ViewEncapsulation,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { merge } from 'rxjs';
import { map } from 'rxjs/operators';

import { TranslatePipe } from '@core/i18n-legacy';
import { TellentAssistantControlService } from '@core/tellent-assistant-logic';
import { TellentAssistantBrandMarkComponent } from '@core/tellent-assistant-ui';
import { DialogOpenerService, FIXED_OVERLAY_LEVEL, RtTooltip } from '@core/user-interface-legacy';

@Component({
  selector: 'tellent-assistant-trigger',
  imports: [TellentAssistantBrandMarkComponent, RtTooltip, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation -- the trigger button is portaled to a body-attached overlay; styles must reach it. Selectors are namespaced (tellent-assistant-trigger*).
  encapsulation: ViewEncapsulation.None,
  templateUrl: './tellent-assistant-trigger.component.html',
  styleUrl: './tellent-assistant-trigger.component.less',
  host: {
    class: 'tellent-assistant-trigger-anchor',
    '[class.tellent-assistant-trigger-anchor--collapsed]': 'effectiveCollapsed()',
  },
})
export class TellentAssistantTriggerComponent {
  private readonly overlay = inject(Overlay);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly dialogOpener = inject(DialogOpenerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly control = inject(TellentAssistantControlService);
  private readonly triggerTpl = viewChild.required<TemplateRef<unknown>>('triggerTpl');
  private readonly dialogOpen = toSignal(
    merge(
      this.dialogOpener.afterOpen.pipe(map(() => true)),
      this.dialogOpener.afterAllClosed.pipe(map(() => false)),
    ),
    { initialValue: this.dialogOpener.hasDialogsOpen },
  );

  protected readonly effectiveCollapsed = computed(
    () => this.collapsed() || this.dialogOpen() || this.control.open(),
  );

  public readonly collapsed = input<boolean>(false);
  public readonly triggered = output<void>();
  public readonly leave = output<MouseEvent>();

  private overlayRef: OverlayRef | null = null;

  constructor() {
    afterNextRender(() => this.attach());
    this.destroyRef.onDestroy(() => this.detach());
  }

  protected onClick(): void {
    this.triggered.emit();
  }

  private attach(): void {
    if (this.overlayRef) return;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'bottom' },
      ])
      .withPush(false);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: 'tellent-assistant-trigger-overlay',
    });
    this.overlayRef.hostElement.style.zIndex = `${FIXED_OVERLAY_LEVEL}`;
    this.overlayRef.attach(new TemplatePortal(this.triggerTpl(), this.viewContainerRef));
  }

  private detach(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }
}
